/** Karekod doğrulama ekranının veri tipleri (modal ve detay penceresi ortak kullanır). */

/** İTS doğrulama / durum sorgulama sonucunun zenginleştirilmiş hâli (its-proxy yanıtı). */
export interface ItsOutcome {
  responseCode: string
  responseMessage: string
  responseDescription: string | null
  gln1: string | null
  gln2: string | null
  gln1CompanyName: string | null
  gln2CompanyName: string | null
  productName: string | null
  manufacturerName: string | null
  price: number | null
  productFound: boolean
}

export type ItsQueryState = 'idle' | 'loading' | 'done' | 'failed'

/** Merkezi katalog + eczane stoğundan gelen ürün bilgisi (GTIN başına önbelleklenir). */
export interface CatalogInfo {
  /** Eczane veritabanındaki ürün kimliği; ürün kartını açabilmek için gerekir. */
  productId: string | null
  drugName: string | null
  manufacturerName: string | null
  psf: number | null
  stockQuantity: number | null
  found: boolean
}

/** Listeye eklenen tek bir okutma. */
export interface ScannedCode {
  id: string
  /** Okutulan karekodun ham hâli; listede ve detayda olduğu gibi gösterilir. */
  raw: string
  gtin: string | null
  serialNumber: string | null
  lotNumber: string | null
  expiryDate: string | null
  isValid: boolean
  parseErrors: string[]
  /** Aynı karekod tekrar okutulursa satır çoğaltılmaz, sayaç artar. */
  scanCount: number
  lastScannedAt: number
  verifyState: ItsQueryState
  verify: ItsOutcome | null
  verifyError: string | null
  statusState: ItsQueryState
  status: ItsOutcome | null
  statusError: string | null
}

/** Aynı kutuyu tanımlayan anahtar: GTIN + seri no, yoksa ham karekod. */
export function scannedCodeKey(entry: Pick<ScannedCode, 'gtin' | 'serialNumber' | 'raw'>): string {
  if (entry.gtin && entry.serialNumber) return `${entry.gtin}|${entry.serialNumber}`
  return entry.raw.trim()
}

/** Backend GtinNormalizer ile aynı kural: 13 haneli barkod başına 0 alır. */
export function normalizeGtinTo14(value: string | null | undefined): string | null {
  if (!value?.trim()) return null
  const digits = value.replace(/\D/g, '')
  if (digits.length === 13) return `0${digits}`
  if (digits.length === 14) return digits
  return null
}

/** ISO tarihi (YYYY-MM-DD) gün/ay/yıl olarak gösterir. */
export function formatIsoDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '—'
  const parts = isoDate.split('-')
  if (parts.length !== 3) return isoDate
  return `${parts[2]}.${parts[1]}.${parts[0]}`
}

/**
 * İTS mesajlarında geçen GLN kodlarını, biliniyorsa firma adıyla değiştirir;
 * eczacı "8680001..." yerine karşı tarafın adını görmeli.
 */
export function replaceGlnWithCompanyName(outcome: ItsOutcome | null): string {
  if (!outcome) return '—'
  let text = outcome.responseMessage || 'Bilinmeyen durum'

  const swap = (gln: string | null, companyName: string | null, label: 'GLN1' | 'GLN2') => {
    if (!gln) return
    const replacement = companyName ?? gln
    text = text.split(gln).join(replacement)
    text = text.replace(new RegExp(label, 'gi'), replacement)
    if (companyName) {
      text = text.replace(new RegExp(`${escapeForRegExp(companyName)}\\s+kodlu\\s+`, 'gi'), `${companyName} `)
    }
  }

  swap(outcome.gln1, outcome.gln1CompanyName, 'GLN1')
  swap(outcome.gln2, outcome.gln2CompanyName, 'GLN2')
  return text
}

/** Sahiplik sorgusundaki son sahip; eczane adı varsa o, yoksa GLN. */
export function sonSahipMetni(outcome: ItsOutcome | null): string | null {
  if (!outcome) return null
  const ad = outcome.gln1CompanyName?.trim()
  if (ad) return ad
  const gln = outcome.gln1?.trim()
  return gln || null
}

/** Askıda kalan karşı taraf; eczane adı varsa o, yoksa GLN. */
export function aradaKalanSahipMetni(outcome: ItsOutcome | null): string | null {
  if (!outcome) return null
  const ad = outcome.gln2CompanyName?.trim()
  if (ad) return ad
  const gln = outcome.gln2?.trim()
  return gln || null
}

/** Doğrulama ile sahiplik aynı İTS cümlesini dönüyorsa alt alta tekrarlama. */
export function itsMesajlariAyni(a: ItsOutcome | null, b: ItsOutcome | null): boolean {
  if (!a || !b) return false
  return replaceGlnWithCompanyName(a).trim() === replaceGlnWithCompanyName(b).trim()
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
